import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import api from "../api/client";
import {
  ArrowLeft,
  BadgeCheck,
  BriefcaseBusiness,
  CalendarDays,
  Clock3,
  Globe2,
  MapPin,
  PackageCheck,
  Star,
  UserRound,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import StarRating from "../components/ui/StarRating";
import logger from "../utils/logger";
import {
  formatServiceCurrency,
  getFreelancerProfileReference,
  getFreelancerServiceSummary,
  getFreelancerServiceTitle,
  getServiceDeliveryLabel,
  getServiceFreelancer,
  listFreelancerServices,
} from "../api/freelancerServices";
import "./FreelancerProfilePage.css";

const avatarFallback = "https://ui-avatars.com/api/?background=183b4e&color=ffffff&size=240&name=Freelancer";
const serviceFallbackImage = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=900&q=80";

function sameReference(service, reference) {
  return getFreelancerProfileReference(service).toLowerCase() === String(reference || "").toLowerCase();
}

function providerValue(provider, ...keys) {
  for (const key of keys) {
    if (provider?.[key]) return provider[key];
  }
  return "";
}

export default function FreelancerProfilePage() {
  const { freelancerRef } = useParams();
  const { state } = useLocation();
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [services, setServices] = useState([]);
  const [profile, setProfile] = useState(state?.freelancer || getServiceFreelancer(state?.service));
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    async function loadProfile() {
      try {
        setLoading(true);
        
        let directProfile = null;
        try {
          const profileRes = await api.apiGet(`freelancers/profile/${freelancerRef}`);
          const pData = profileRes?.data || profileRes;
          if (pData) {
            directProfile = Array.isArray(pData) ? pData[0] : pData;
          }
        } catch (profileErr) {
          logger.warn("Failed to fetch freelancer profile directly from API", profileErr);
        }

        const allServices = await listFreelancerServices({ cache: true, ttl: 15_000 });
        if (!active) return;
        
        const providerServices = allServices.filter((service) => sameReference(service, freelancerRef));
        setServices(providerServices);
        setProfile((current) => directProfile || current || getServiceFreelancer(providerServices[0]));
        setFailed(!directProfile && !providerServices.length && !state?.freelancer);
      } catch (error) {
        logger.error("Freelancer profile load error", error);
        if (active) setFailed(!state?.freelancer);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadProfile();
    return () => { active = false; };
  }, [freelancerRef, state?.freelancer]);

  const displayName = providerValue(profile, "name", "full_name", "display_name") || (isAr ? "مستقل" : "Freelancer");
  const title = providerValue(profile, "title", "headline", "profession", "job_title") || (isAr ? "مقدّم خدمات مستقل" : "Freelance service provider");
  const bio = providerValue(profile, "bio", "about", "description") || (isAr ? "يعرض هذا المستقل خدماته وخبراته المهنية على المنصة." : "This freelancer shares their professional services and expertise on the platform.");
  const avatar = providerValue(profile, "avatar_url", "avatar", "photo_url", "image") || avatarFallback;
  const rating = useMemo(() => {
    const direct = Number(providerValue(profile, "average_rating", "rating"));
    if (direct) return direct;
    const rated = services.filter((service) => Number(service?.average_rating || service?.avg_rating));
    return rated.length ? rated.reduce((sum, service) => sum + Number(service.average_rating || service.avg_rating || 0), 0) / rated.length : 0;
  }, [profile, services]);
  const completedOrders = services.reduce((sum, service) => sum + Number(service?.orders_count || 0), 0);
  const categories = [...new Set(services.map((service) => service?.category).filter(Boolean))];
  const location = providerValue(profile, "location", "country", "city");
  const languages = providerValue(profile, "languages", "language");
  const memberSince = providerValue(profile, "created_at", "member_since");

  if (loading) return <div className="container py-5 text-center"><div className="spinner-border text-primary mb-3" /><p>{isAr ? "جارٍ تحميل الملف الشخصي..." : "Loading profile..."}</p></div>;
  if (failed) return <Navigate to="/freelancers" replace />;

  return (
    <div className="freelancer-profile-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="freelancer-profile-shell">
        <Link to="/freelancers" className="freelancer-profile-back"><ArrowLeft size={16} />{isAr ? "الرجوع إلى الخدمات" : "Back to services"}</Link>

        <section className="freelancer-profile-hero">
          <div className="freelancer-profile-avatar-wrap">
            <img src={avatar} alt={displayName} onError={(event) => { event.currentTarget.src = avatarFallback; }} />
            <span className="freelancer-profile-online" title={isAr ? "متاح" : "Available"} />
          </div>
          <div className="freelancer-profile-identity">
            <div className="freelancer-profile-name"><h1>{displayName}</h1><BadgeCheck size={21} aria-label={isAr ? "حساب موثّق" : "Verified profile"} /></div>
            <p className="freelancer-profile-title">{title}</p>
            <div className="freelancer-profile-rating"><StarRating value={rating} size={17} showValue /><span>({completedOrders} {isAr ? "طلبًا" : "orders"})</span></div>
          </div>
          <a className="freelancer-profile-contact" href="#freelancer-services">{isAr ? "عرض الخدمات" : "View services"}</a>
        </section>

        <div className="freelancer-profile-layout">
          <aside className="freelancer-profile-sidebar">
            <div className="freelancer-profile-panel">
              <h2>{isAr ? "عن المستقل" : "About the freelancer"}</h2>
              <p>{bio}</p>
              <div className="freelancer-profile-details">
                {location && <div><MapPin size={17} /><span>{location}</span></div>}
                {languages && <div><Globe2 size={17} /><span>{Array.isArray(languages) ? languages.join("، ") : languages}</span></div>}
                {memberSince && <div><CalendarDays size={17} /><span>{isAr ? "عضو منذ " : "Member since "}{String(memberSince).slice(0, 10)}</span></div>}
              </div>
            </div>
            <div className="freelancer-profile-panel freelancer-profile-stats">
              <div><BriefcaseBusiness size={18} /><strong>{services.length}</strong><span>{isAr ? "خدمات نشطة" : "Active services"}</span></div>
              <div><PackageCheck size={18} /><strong>{completedOrders}</strong><span>{isAr ? "طلبات" : "Orders"}</span></div>
              <div><Star size={18} /><strong>{rating ? rating.toFixed(1) : "—"}</strong><span>{isAr ? "التقييم" : "Rating"}</span></div>
            </div>
            {categories.length > 0 && <div className="freelancer-profile-panel"><h2>{isAr ? "المهارات والخدمات" : "Skills & services"}</h2><div className="freelancer-profile-tags">{categories.map((item) => <span key={item}>{item}</span>)}</div></div>}
          </aside>

          <main id="freelancer-services">
            <div className="freelancer-profile-section-head"><div><span>{isAr ? "الخدمات المتاحة" : "Available services"}</span><h2>{isAr ? `خدمات ${displayName}` : `${displayName}'s services`}</h2></div><strong>{services.length}</strong></div>
            {services.length ? <div className="freelancer-profile-services-grid">{services.map((service) => {
              const ref = service?.slug || service?.id;
              const serviceTitle = getFreelancerServiceTitle(service, isAr);
              return <Link to={`/freelancers/${ref}`} className="freelancer-profile-service-card" key={service.id || ref}>
                <img src={service?.cover_image || serviceFallbackImage} alt={serviceTitle} onError={(event) => { event.currentTarget.src = serviceFallbackImage; }} />
                <div><p>{service?.category || (isAr ? "خدمة احترافية" : "Professional service")}</p><h3>{serviceTitle}</h3><span className="freelancer-profile-service-summary">{getFreelancerServiceSummary(service, isAr)}</span><footer><span><Clock3 size={15} />{getServiceDeliveryLabel(service, isAr)}</span><strong>{formatServiceCurrency(service?.price || 0, service?.currency || "USD")}</strong></footer></div>
              </Link>;
            })}</div> : <div className="freelancer-profile-empty"><UserRound size={28} /><p>{isAr ? "لا توجد خدمات منشورة حاليًا." : "No published services yet."}</p></div>}
          </main>
        </div>
      </div>
    </div>
  );
}
